import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"
import { setHidden } from "../lib/display_toggle"

// Keeps the Conversations red-circle unread counter live: refetches the
// count (debounced) whenever a CRM realtime event or an in-app read action
// fires. The server-rendered number is cached/stale-by-design; this is the
// correction layer.
//
// A hidden tab never fetches (owner, 2026-10-19, #18159): refetching in EVERY
// open tab on every CRM event lets an agent with many tabs burst the shared
// request limit. A hidden tab only remembers that its count
// is stale and refreshes once when it is shown again.
export default class extends Controller {
  static values = { url: String }

  connect() {
    this.refresh = this.refresh.bind(this)
    this.scheduleRefresh = this.scheduleRefresh.bind(this)
    document.addEventListener("goat:realtime-event", this.maybeRefresh = (e) => {
      const detail = e.detail || {}
      if (typeof detail.crm_event === "string" && detail.crm_event.startsWith("crm_")) this.scheduleRefresh()
    })
    document.addEventListener("goat:crm-unread-changed", this.scheduleRefresh)
    document.addEventListener("visibilitychange", this.refreshIfShownStale = () => {
      if (document.hidden || !this.stale) return
      this.stale = false
      this.scheduleRefresh()
    })
    // The SSR count is cached/stale-by-design (SidebarBadgeCounts::CACHE_TTL);
    // refresh once on load so a reply that landed just before this page
    // render doesn't sit stale until the next realtime event or cache expiry.
    this.scheduleRefresh()
  }

  disconnect() {
    document.removeEventListener("goat:realtime-event", this.maybeRefresh)
    document.removeEventListener("goat:crm-unread-changed", this.scheduleRefresh)
    document.removeEventListener("visibilitychange", this.refreshIfShownStale)
    clearTimeout(this.debounce)
  }

  scheduleRefresh() {
    clearTimeout(this.debounce)
    this.debounce = setTimeout(this.refresh, 1500)
  }

  async refresh() {
    if (document.hidden) {
      this.stale = true
      return
    }
    const response = await get(this.urlValue, { responseKind: "json" })
    if (!response.ok) return
    const { unread } = await response.json
    this.element.querySelectorAll("[data-crm-unread-badge]").forEach((badge) => {
      badge.textContent = String(unread)
      setHidden(badge, !(unread > 0))
    })
  }
}
